import { observer } from 'mobx-react-lite';
import React, { useContext, useState } from 'react';
import { useSearchParamsUtils } from 'app/hooks/useSearchParamsUtils';
import { useNavigate } from 'react-router-dom';
import PageInnerContainer from 'pages/components/pageInnerContainer';
import ExplorerHeader from 'pages/explorerPage/explorerHeader';
import EditFolderModalController from 'widgets/folders/editFolderModalController';
import { FolderModel } from 'entities/folder/model/folderModel';
import { MobxContext } from 'shared/lib/contexts/mobxContext';
import { useBooleanScreens } from 'shared/hooks/useBooleanScreens';
import SearchFallback from 'pages/searchFallback';
import ContentGridController from 'widgets/grids/contentGridController';
import CreateFolderModalController from 'widgets/folders/createFolderModalController';
import mobxStore from 'app/store/rootStore';
import ActionInlineButton from 'shared/lib/components/controls/buttons/actionInlineButton';
import IconCirclePlus from 'shared/icons/iconCirclePlus';
import { useTranslation } from 'react-i18next';

export default observer(function ExplorerFolder({
  folderModel,
  sectionId,
}: {
  folderModel: FolderModel;
  sectionId: string;
}) {
  const {
    mobxStore: {
      authStore: { user },
    },
  } = useContext(MobxContext);
  const { mobxStore } = useContext(MobxContext);
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [isEditingFolder, setIsEditingFolder] = useState(false);
  const { getFolderSearchParams, getSectionSearchParams } =
    useSearchParamsUtils();

  const { isLessLaptop } = useBooleanScreens();
  const isOnlyView = mobxStore.authStore.user?.isOnlyView;
  const [isCreateFolder, setIsCreateFolder] = useState(false);

  return folderModel?.data ? (
    <SearchFallback>
      <PageInnerContainer>
        <ExplorerHeader
          name={folderModel.data.name}
          isButtonsDisabled={user?.isOnlyView || isLessLaptop}
          onSettings={() => setIsEditingFolder(true)}
          onGoToUpload={() => {
            navigate({
              pathname: '/upload/',
              search: getFolderSearchParams({
                folderId: folderModel.id,
              }),
            });
          }}
        ></ExplorerHeader>

        {isOnlyView || isLessLaptop ? null : (
          <ActionInlineButton
            Icon={IconCirclePlus}
            onClick={() => setIsCreateFolder(true)}
          >
            {t('noCategory.createNewFolder')}
          </ActionInlineButton>
        )}

        <ContentGridController
          items={folderModel.content}
          sort={{ key: folderModel.sortKey, order: folderModel.sortOrder }}
          onSortChange={(val) => {
            folderModel.sortKey = val.key;
            folderModel.sortOrder = val.order;
            folderModel.getContent({ in_trash: null });
          }}
          afterFolderMerge={() => {
            folderModel.getContent({ in_trash: null });
          }}
          afterFolderUpdate={() => {
            folderModel.getContent({ in_trash: null });
          }}
          afterFolderDelete={(id) => {
            folderModel.localDeleteFolders([id]);
          }}
          afterFileDelete={(id) => folderModel.localDeleteFile(id)}
          afterFileUpdate={() => {
            folderModel.getContent({ in_trash: null });
          }}
          afterTableDelete={(id) => folderModel.localDeleteTable(id)}
          afterTableUpdate={() => {
            folderModel.getContent({ in_trash: null });
          }}
          afterFolderCreate={() => {
            folderModel.getContent({ in_trash: null });
          }}
        ></ContentGridController>

        {isEditingFolder && (
          <EditFolderModalController
            updatable={folderModel.data}
            handleClose={() => setIsEditingFolder(false)}
            afterFolderDelete={() => {
              navigate({
                pathname: '/explorer/',
                search: getSectionSearchParams({
                  sectionId,
                }),
              });
            }}
            afterFolderMerge={(resultFolder) => {
              if (resultFolder.section_id) {
                navigate({
                  pathname: '/explorer/',
                  search: getFolderSearchParams({
                    folderId: resultFolder.id,
                  }),
                });
                return;
              }
            }}
            afterFolderUpdate={() => {
              folderModel.fetch({ in_trash: null });
              mobxStore.generateExplorerPageKey();
            }}
          ></EditFolderModalController>
        )}
      </PageInnerContainer>
      {isCreateFolder && (
        <CreateFolderModalController
          isOpen={true}
          handleClose={() => setIsCreateFolder(false)}
          sectionId={folderModel.sectionId || sectionId}
          parentId={folderModel.id}
          afterFolderCreate={(newFolderModel) => {
            folderModel.localAddFolder(newFolderModel);
          }}
        ></CreateFolderModalController>
      )}
    </SearchFallback>
  ) : (
    <></>
  );
});
